/**
 * Material lookups shared by every entity, kept separate from `build.ts` so
 * entity modules never import the builder that calls them.
 */

import {
  signatureRamp,
  volumeRamp,
  type DepthPalette,
  type SignatureRamp,
  type VolumeRamp,
} from '../render/palette.ts';
import { hex, type Color } from '../render/surface.ts';
import type { EntityState } from './entity.ts';

/** The structure volume ramp at a depth. Every structure shares it (doc 10). */
export function rampFor(depth: DepthPalette): VolumeRamp {
  return volumeRamp(depth.structureBase);
}

export function sigRamp(colour: string): SignatureRamp {
  return signatureRamp(hex(colour));
}

/**
 * Which step of the signature light a state shows.
 *
 * Global, not per entity: "function is readable by light alone at any zoom"
 * (doc 10 rule 6) only holds if every entity agrees what "working" looks like.
 */
export function signatureStep(state: EntityState): 'off' | 'dim' | 'on' | 'hot' {
  switch (state) {
    case 'working':
    case 'tracking':
      return 'on';
    case 'firing':
      return 'hot';
    case 'starved':
    case 'destroyed':
      return 'off';
    case 'dry':
      return 'dim';
    default:
      return 'dim';
  }
}

export function signatureColor(ramp: SignatureRamp, state: EntityState): Color | null {
  switch (signatureStep(state)) {
    case 'off':
      return null;
    case 'dim':
      return ramp.dim;
    case 'on':
      return ramp.on;
    case 'hot':
      return ramp.hot;
  }
}

/**
 * The two signature families, so a glance separates violence from production
 * (doc 10 rule 6, extended in stage 0):
 *   warm GOLD  -> economy buildings
 *   warm RED   -> towers
 * Both stay warm, because player = warm light and enemies = cold/toxic light.
 */
export const SIGNATURE_ECONOMY = '#ffd45e';
export const SIGNATURE_TOWER = '#ff7a3d';
